import { useEffect } from "react"
import { SafeAreaView } from "react-native-safe-area-context"
import { useLocalSearchParams, Link, router } from "expo-router"
import { ProductType } from "@white-labels/input"
import { Show, useObservable } from "@legendapp/state/react"
import { View, Pressable, ActivityIndicator } from "react-native"
import * as Clipboard from "expo-clipboard"
import * as Burnt from "burnt"

import { Header, Text, ScrollView, MCIcon, InventoryCollectionPickModal, MenuActions, DerivedProductCard } from "@src/components"
import { ProductImageView } from "components/product-image-view"
import { WeightDefinedProductForm } from "@src/components/weight-defined-product-form"
import { trpc } from "utils/trpc"
import { ar } from "@src/i18n/ar"
import { formatPrice, getScaleCode } from "@src/utils"
import { Alert } from "@src/components/ui"

export default function ProductDetails() {
  const params = useLocalSearchParams<{ id: string }>()
  const showWeightDefinedProductModal$ = useObservable(false)
  const id = Number.parseInt(params.id, 10)
  const product = trpc.products.get.useQuery({ id }, { retry: false })
  const utils = trpc.useUtils()

  const setStatus = trpc.products.setStatus.useMutation()

  const isInactive = product.data?.status === "inactive"

  const handleDelete = async () => {
    try {
      await setStatus.mutateAsync({ id, status: "inactive" })
      utils.products.list.invalidate()
      utils.products.get.invalidate({ id })
      router.back()
      Burnt.toast({ title: ar.productDeleted })
      return undefined
    } catch (error) {
      return error instanceof Error ? error.message : ar.somethingWentWrong
    }
  }

  const handleRestore = () => {
    Alert.alert(ar.restoreProduct, ar.restoreProductConfirmation, [
      { text: ar.cancel, style: "cancel" },
      {
        text: ar.restoreProduct,
        style: "default",
        onPress: () => {
          setStatus.mutate(
            { id, status: "active" },
            {
              onSuccess: () => {
                utils.products.list.invalidate()
                utils.products.get.invalidate({ id })
                router.back()
                Burnt.toast({ title: ar.productRestored })
              },
              onError: (error) => {
                Burnt.toast({ title: error.message ?? ar.somethingWentWrong })
              },
            },
          )
        },
      },
    ])
  }

  const header = (
    <Header
      title={ar.productDetails}
      className="z-10"
      right={
        isInactive ? (
          <View className="flex-row h-full items-center">
            <Pressable onPress={handleRestore} className="h-full px-3 justify-center active:opacity-70" disabled={setStatus.isPending}>
              <Text className="text-green-600 font-bold">{ar.restoreProduct}</Text>
            </Pressable>
          </View>
        ) : (
          <View className="relative h-full justify-center">
            <MenuActions
              itemName={product.data?.name ?? ""}
              onDelete={handleDelete}
              onEdit={() => product.data?.id && router.push(`/dashboard/products/${product.data.id}/edit`)}
              isPending={setStatus.isPending}
            />
          </View>
        )
      }
    />
  )

  useEffect(() => {
    if (product.data?.type === "WEIGHT_DEFINED_PRODUCT") {
      router.replace(`/dashboard/products/${product.data.baseProductId}`)
    }
  }, [product.data])

  if (product.data?.type === "WEIGHT_DEFINED_PRODUCT") {
    return null
  }

  if (product.isLoading) {
    return (
      <>
        {header}
        <ActivityIndicator className="mt-8" />
      </>
    )
  }

  if (!product.data) {
    return (
      <>
        {header}
        <Pressable className="mt-auto mb-auto border mx-2 py-4 px-2 active:opacity-50" onPress={() => product.refetch()}>
          <Text className="text-gray-600 text-center">
            {ar.somethingWentWrong} {ar.tryAgain}
          </Text>
        </Pressable>
      </>
    )
  }

  const showOriginalPrice = product.data.originalPrice && product.data.originalPrice > (product.data.price ?? 0)

  const colors = PRODUCT_TYPE_COLOR_CLASSES[product.data.type]
  const productType = (
    <>
      <View className="flex-row justify-between items-center gap-x-2">
        <View className={`border ${colors.bg} ${colors.border} flex-1 rounded py-2 px-4 mb-3`}>
          <Text className={`${colors.text} font-bold text-center`}>{ar[product.data.type]}</Text>
        </View>
        <View className={`flex-1 px-5`} />
      </View>

      <Show if={showWeightDefinedProductModal$}>
        <WeightDefinedProductForm
          baseProductId={id}
          onClose={() => {
            showWeightDefinedProductModal$.set(false)
          }}
        />
      </Show>
    </>
  )

  const pricing = (
    <View className="bg-white rounded p-6 mb-2">
      <Text className="text-lg md:text-xl font-semibold text-gray-900 mb-2 ">{ar.pricing}</Text>

      <View className="space-y-3">
        {product.data.price ? (
          <View className="flex-row justify-between items-center py-2">
            <Text className="text-gray-600 ">{ar.price}</Text>
            <Text className="text-xl md:text-2xl font-bold text-green-600">
              {formatPrice(product.data.price)} {ar.egp}
            </Text>
          </View>
        ) : null}

        {showOriginalPrice ? (
          <View className="flex-row justify-between items-center py-2">
            <Text className="text-gray-600 ">{ar.originalPrice}</Text>
            <Text className="text-base md:text-lg text-gray-500 line-through">
              {formatPrice(product.data.originalPrice)} {ar.egp}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  )

  const derived = product.data?.derived ?? []
  const derivedProducts = (derived.length > 0 || product.data.type === "WEIGHT_PRODUCT") && (
    <View className="bg-white border border-gray-200 rounded p-2 mb-2">
      <View className="flex-row justify-between items-center">
        <Text className="text-lg md:text-xl font-semibold text-gray-900 mb-2">{ar.derivedProducts}</Text>
        {product.data.type === "WEIGHT_PRODUCT" && (
          <Pressable onPress={() => showWeightDefinedProductModal$.set(true)} className="px-4 active:opacity-70">
            <Text className="text-blue-500 font-bold">{ar.addWeightDefinedProduct}</Text>
          </Pressable>
        )}
      </View>

      <View className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-4">
        {derived.map((dp) => (
          <DerivedProductCard key={dp.id} dp={dp} baseId={id} baseStatus={product.data?.status} />
        ))}
      </View>
    </View>
  )

  return (
    <>
      {header}
      <ScrollView contentContainerClassName="p-4 md:p-6 lg:p-8 max-w-content-wide mx-auto w-full" onRefresh={product.refetch}>
        <View className="md:flex-row md:gap-6 lg:gap-8">
          <View className="md:w-1/3 lg:w-1/4">
            <View className="bg-white border border-gray-200 rounded p-2 mb-2">
              <ProductImageView images={product.data.images} id={product.data.id} />
            </View>
          </View>

          <View className="web:flex-1">
            <View className="bg-white border border-gray-200 rounded p-6 mb-2">
              <Text className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{product.data.name}</Text>
              {productType}

              <Text className="text-gray-600 mb-2 leading-6 text-base">{product.data.description ?? ar.noProductDescription}</Text>

              <View className="md:grid md:grid-cols-2 md:gap-4">
                <Pressable
                  className="mb-2"
                  onLongPress={() => {
                    Clipboard.setStringAsync(product.data.upc)
                    Burnt.toast({ title: ar.barcodeCopied })
                  }}
                >
                  <Text className="text-sm font-medium text-gray-500 mb-1">{ar.barcode}</Text>
                  <Text className="text-base md:text-lg font-mono bg-gray-100 p-2 rounded hover:bg-gray-200" forceRight>
                    {product.data.upc}
                  </Text>
                </Pressable>

                {product.data.type === "WEIGHT_PRODUCT" ? (
                  <>
                    <View className="mb-2">
                      <Text className="text-sm font-medium text-gray-500 mb-1">{ar.scaleCode}</Text>
                      <Text className="text-base md:text-lg font-mono bg-gray-100 p-2 rounded hover:bg-gray-200">{getScaleCode(product.data.upc)}</Text>
                    </View>
                    <View className="mb-2">
                      <Text className="text-sm font-medium text-gray-500 mb-1">{ar.weight}</Text>
                      <Text className="text-base md:text-lg text-gray-900 ">
                        {product.data.type === "WEIGHT_PRODUCT" ? `${product.data.value / 1000} ${ar.kg}` : `${product.data.value} ${ar.gms}`}
                      </Text>
                    </View>
                  </>
                ) : null}

                {product.data.brand ? (
                  <View className="mb-2">
                    <Text className="text-sm font-medium text-gray-500 mb-1">{ar.brand}</Text>
                    <Text className="text-base md:text-lg text-gray-900 ">{product.data.brand.name}</Text>
                  </View>
                ) : null}
              </View>

              {product.data.base ? (
                <Link href={`/dashboard/products/${product.data.base.id}`} asChild>
                  <Pressable className="mb-2 rounded active:opacity-50">
                    <Text className="text-sm font-bold text-gray-500 mb-1 ">{ar.baseProduct}</Text>
                    <Text className="text-base md:text-lg text-blue-500 ">{product.data.base.name}</Text>
                  </Pressable>
                </Link>
              ) : null}
            </View>

            {pricing}
            <ProductInventoryCollections id={id} />
          </View>
        </View>

        {derivedProducts}
        <SafeAreaView />
      </ScrollView>
    </>
  )
}

function ProductInventoryCollections({ id }: { id: number }) {
  const utils = trpc.useUtils()
  const iCollections = trpc.inventoryCollections.forProduct.useQuery({ productId: id })
  const addProductToCollection = trpc.inventoryCollections.addProductToCollections.useMutation()
  const showModal$ = useObservable(false)

  function show() {
    showModal$.set(true)
  }

  function hide() {
    showModal$.set(false)
  }

  const isLoading = iCollections.isLoading
  const data = iCollections.data ?? []

  return (
    <>
      <View className="bg-white rounded mb-2">
        <View className="flex-row justify-between items-center pt-2">
          <Text className="text-lg md:text-xl font-semibold text-gray-900 mb-2 px-2">{ar.inventoryCollections}</Text>
          <Pressable className="px-4 active:opacity-70" onPress={show}>
            <MCIcon name="plus" size={24} color="#2563eb" />
          </Pressable>
        </View>

        {isLoading ? (
          <ActivityIndicator className="mt-2" />
        ) : data.length === 0 ? (
          <Text className="text-gray-600 text-center py-3 font-bold">{ar.notPartOfAnyInventoryCollections}</Text>
        ) : null}

        <View className="flex-row gap-x-1 p-1">
          {data.map((collection) => (
            <Link href={`/dashboard/inventory/collections/${collection.id}`} asChild key={collection.id}>
              <Pressable className="bg-gray-100 px-3 py-1 rounded active:opacity-50">
                <Text className="text-black">{collection.name}</Text>
              </Pressable>
            </Link>
          ))}
        </View>
      </View>
      <Show if={showModal$}>
        <InventoryCollectionPickModal
          hide={hide}
          onChange={(data) => {
            addProductToCollection.mutate(
              { productId: id, collectionIds: data },
              {
                onSuccess: () => {
                  iCollections.refetch()

                  data.forEach((collection) => {
                    utils.inventoryCollections.get.invalidate({ id: collection })
                    utils.inventoryCollections.listProducts.invalidate({ id: collection })
                  })
                },
              },
            )
            hide()
          }}
          value={data.map((c) => c.id) ?? []}
        />
      </Show>
    </>
  )
}

const PRODUCT_TYPE_COLOR_CLASSES = {
  [ProductType.LOCAL_PRODUCT]: {
    bg: "bg-orange-100",
    border: "border-orange-200",
    text: "text-orange-800",
  },

  [ProductType.STANDARD_PRODUCT]: {
    bg: "bg-blue-100",
    border: "border-blue-200",
    text: "text-blue-800",
  },

  [ProductType.WEIGHT_PRODUCT]: {
    bg: "bg-green-100",
    border: "border-green-200",
    text: "text-green-800",
  },

  [ProductType.WEIGHT_DEFINED_PRODUCT]: {
    bg: "bg-green-100",
    border: "border-green-200",
    text: "text-green-800",
  },
}
